import React from "react";
import PropTypes from "prop-types";
import { Modal, Spin, message } from "antd";
import moment from "moment";
import CaseService from "services/case-service";
import FacilityAccountService from "services/facility-account-service";
import CompanyLosServices from "modules/companies/services/company-los-services";
import { BUSINESS_PRODUCT_TYPE_UPDATED } from "constants/enums";
import {
    Card,
    KeyValue,
    Pill,
    SectionHeading,
    Segmented,
    EmptyState,
    StatusBadge,
    Icon,
    ICONS,
    Toggle,
    CardHead,
    Metric
} from "./ui";
import {
    money,
    date,
    isoDate,
    chargeText,
    personName,
    parseSchemeDescription,
    get,
    getIn,
    toArray
} from "./data-utils";

/**
 * Account / Facility — the commercials the kit is generated from.
 *
 * This panel is built from the workspace's own primitives rather than
 * embedding the Account/Facility Details screen: that screen is an antd form
 * stack whose rows, labels and tables line up on their own grid, and dropping
 * it inside the workspace left two different layouts stacked on one page.
 * Everything here sits on the workspace's card / key-value grid, so a column
 * in one card lines up with the same column in the next.
 *
 * It is not a read-only copy either. The operator actions the screen owns —
 * changing the fee program, adding the PF UTR, skipping the POD stage, moving
 * the case to LMS, terminating a sanction condition and generating, saving or
 * resetting the term-loan EMI schedule — run here against the same endpoints,
 * with the same gating, so the two surfaces cannot drift.
 */

const EMI_COLUMNS = [
    { key: "instalment_nbr", label: "EMI" },
    { key: "emi_amount", label: "EMI Amount", money: true },
    { key: "principal_amount", label: "Principal", money: true },
    { key: "interest_amount", label: "Interest", money: true },
    { key: "principal_receivable", label: "Outstanding", money: true },
    { key: "interest_paid_till_date", label: "Cumulative Interest", money: true },
    { key: "tentative_due_date", label: "Tentative Due Date", date: true },
    { key: "upfront_interest_amount", label: "Upfront Interest", money: true }
];

// Mirrors the Account/Facility Details screen so the same case reaches the
// same fee catalogue from either surface.
const FEE_PRODUCT_TYPE = { SCF: 1, TERM_LOAN: 2, VENDOR_FINANCE: 3, FACTORING: 4 };

const SKIP_POD_STAGE_IDS = [11, 57, 35, 24, 13, 17];
const SKIP_POD_READONLY_STAGE_IDS = [57, 35, 24, 13, 17];

// The stages at which the screen saves parameters instead of generating a
// schedule (the case is past generation and only the saved terms matter).
const SCHEDULE_SAVE_STAGE_IDS = [7, 8];

const INSTALMENT_FREQUENCY = { 1: "Day set", 2: "Monthly" };
const INSTALMENT_DATE_TYPE = { 1: "Fixed", 2: "Basis disbursal date" };
const INTEREST_TYPE = { 1: "Flat", 2: "Reducing" };

const bpt = key => BUSINESS_PRODUCT_TYPE_UPDATED.get(key);

const labelFor = (map, value) =>
    value === null || value === undefined || value === ""
        ? null
        : Number.isInteger(value)
            ? map[value] || value
            : value;

/**
 * The services in this app reject with the parsed response body rather than an
 * Error, and DRF reports most failures as `{ field: ["..."] }`. Pull the first
 * readable line out of whichever shape arrives.
 */
const firstError = (error, fallback) => {
    if (!error) return fallback;
    if (typeof error === "string") return error;
    const body =
        (error.response && error.response.data) ||
        (error.message && typeof error.message === "object" && error.message) ||
        error;
    if (typeof body === "string") return body;
    if (body && typeof body === "object") {
        if (Array.isArray(body.non_field_errors) && body.non_field_errors.length) {
            return String(body.non_field_errors[0]);
        }
        if (typeof body.message === "string") return body.message;
        if (typeof body.detail === "string") return body.detail;
        const key = Object.keys(body)[0];
        const value = key ? body[key] : null;
        if (Array.isArray(value) && value.length) return String(value[0]);
        if (typeof value === "string") return value;
    }
    return fallback;
};

/* ------------------------------------------------------------- read-only */

/* ------------------------------------------------------------- section */

class AccountFacilitySection extends React.Component {
    state = {
        pricing: null,
        skipPod: { value: false, loading: false },
        skipPodModal: null,
        feePrograms: [],
        feeProgramsLoading: false,
        feeProgram: null,
        feeSaving: false,
        programModal: null,
        utr: null,
        lmsOpen: false,
        lmsSubmitting: false,
        terminateModal: null,
        prefetch: null,
        // Draft for the schedule builder. `tenure` and `disbursalDate` are the
        // only two the operator supplies; everything else is scheme-derived.
        draft: {
            tenure: "",
            disbursalDate: "",
            emiType: "1",
            deductFirst: false,
            moratoriumDays: null
        },
        preview: [],
        busy: null,
        confirmReset: false
    };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.caseId !== this.props.caseId) {
            this.setState(
                {
                    pricing: null,
                    prefetch: null,
                    preview: [],
                    busy: null,
                    skipPod: { value: false, loading: false }
                },
                this.load
            );
            return;
        }
        // The saved terms land after the workspace's own fetch resolves; seed
        // the builder from them so a re-save does not start from blank.
        if (prevProps.loanTerms !== this.props.loanTerms) this.seedDraft();
        // A case only reads as a term loan once its schedule (or the BNPL
        // flag) arrives, which can be after this panel mounted.
        if (!prevProps.hasTermLoan && this.props.hasTermLoan && !this.state.prefetch) {
            this.loadPrefetch();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    setSafeState(next, done) {
        if (this.unmounted) return;
        this.setState(next, done);
    }

    load = () => {
        const { caseId } = this.props;
        if (!caseId) return;
        this.seedDraft();

        CaseService.getLatestPricingApproval(caseId)
            .then(({ data }) => this.setSafeState({ pricing: data || null }))
            .catch(() => this.setSafeState({ pricing: null }));

        if (this.skipPodApplicable()) this.fetchSkipPod();
        if (this.props.hasTermLoan) this.loadPrefetch();
        this.loadFeePrograms();
    };

    /**
     * The processing fee is not a number an operator types — it is whichever
     * fee program the case is put on, so the catalogue is loaded up front and
     * the fee is picked from it.
     */
    loadFeePrograms = () => {
        const sellerId = this.sellerId();
        if (!sellerId) return;
        this.setSafeState({ feeProgramsLoading: true });
        CompanyLosServices.getFee(sellerId, this.feeProductType())
            .then(({ data }) => {
                const rows =
                    (data && Array.isArray(data.results) && data.results) ||
                    (data && Array.isArray(data.data) && data.data) ||
                    (Array.isArray(data) ? data : []);
                this.setSafeState({
                    feePrograms: rows
                        .map(row => {
                            const master = row && row.fee_master;
                            const id = (master && master.id) || master;
                            if (!id) return null;
                            return {
                                id,
                                name:
                                    (master && master.name) ||
                                    row.fee_master_name ||
                                    row.name ||
                                    `Fee program ${id}`,
                                // A program whose slab has ended cannot be
                                // attached to a case.
                                expired: toArray(master && master.configuration).some(
                                    slab =>
                                        slab &&
                                        slab.active_to &&
                                        moment(slab.active_to).isSameOrBefore(moment(), "day")
                                )
                            };
                        })
                        .filter(Boolean),
                    feeProgramsLoading: false
                });
            })
            .catch(() => this.setSafeState({ feePrograms: [], feeProgramsLoading: false }));
    };

    /** `case/<id>/bnpl/prefetch/` — the scheme-derived schedule parameters. */
    loadPrefetch = () => {
        FacilityAccountService.getEmiInstanceValues(this.props.caseId)
            .then(({ data }) =>
                this.setSafeState({ prefetch: (data && data.data) || null }, this.seedDraft)
            )
            .catch(() => this.setSafeState({ prefetch: null }));
    };

    /** Saved terms win over the scheme prefetch, as on the dedicated screen. */
    seedDraft = () => {
        const { loanTerms } = this.props;
        const prefetch = this.state.prefetch || {};
        this.setSafeState(({ draft }) => ({
            draft: {
                tenure:
                    draft.tenure ||
                    (loanTerms && loanTerms.tenure) ||
                    "",
                disbursalDate:
                    draft.disbursalDate ||
                    isoDate(loanTerms && loanTerms.tentative_disbursal_date) ||
                    "",
                emiType: String(
                    (loanTerms && loanTerms.emi_type) || draft.emiType || "1"
                ),
                deductFirst: Boolean(
                    loanTerms
                        ? loanTerms.is_first_instalment_deducted_from_disbursal
                        : draft.deductFirst
                ),
                moratoriumDays:
                    draft.moratoriumDays !== null && draft.moratoriumDays !== undefined
                        ? draft.moratoriumDays
                        : (loanTerms && loanTerms.moratorium_days) !== undefined &&
                          loanTerms &&
                          loanTerms.moratorium_days !== null
                            ? loanTerms.moratorium_days
                            : prefetch.max_moratorium_period !== undefined
                                ? prefetch.max_moratorium_period
                                : null
            }
        }));
    };

    /* ------------------------------------------------------------ gating */

    canEdit() {
        return !this.props.recreditViewOnly;
    }

    skipPodApplicable() {
        return SKIP_POD_STAGE_IDS.includes(Number(this.props.stageId));
    }

    skipPodReadOnly() {
        return (
            !this.canEdit() ||
            SKIP_POD_READONLY_STAGE_IDS.includes(Number(this.props.stageId))
        );
    }

    /** Same gate as the screen: an ops admin on an open or reopened case. */
    canChangeFeeProgram() {
        const { caseDetail, user } = this.props;
        const stageAdmin = (user && user.stage_admin) || [];
        return (
            this.canEdit() &&
            [1, 3].includes(Number(get(caseDetail, "case_status"))) &&
            toArray(stageAdmin).map(Number).includes(11)
        );
    }

    /** LOS to LMS only opens once the case is past login and not already there. */
    losToLmsDisabled() {
        const stage = Number(this.props.stageId);
        return Boolean(
            get(this.props.caseDetail, "is_created_on_lms") ||
                stage < 10 ||
                [18, 19].includes(stage)
        );
    }

    /** Reset is only allowed while the case sits on the ops stage (11). */
    canResetSchedule() {
        // `this_stage` comes back either as the id or as the stage object.
        const stage = getIn(this.props.caseDetail, ["current_case_task", 0, "this_stage"]);
        const stageId = stage && typeof stage === "object" ? get(stage, "id") : stage;
        return Number(stageId) === 11;
    }

    feeProductType() {
        const type = Number(get(this.props.caseDetail, "business_product_type"));
        if ([
            bpt("INVOICE_FINANCE"),
            bpt("PURCHASE_INVOICE_FINANCE"),
            bpt("SALES_INVOICE_FINANCE")
        ].includes(type)) {
            return FEE_PRODUCT_TYPE.SCF;
        }
        if ([
            bpt("WORKING_CAPITAL_DEMAND_LOAN"),
            bpt("SHORT_TERM_LOAN"),
            bpt("CONSUMER_FINANCE"),
            bpt("LAP"),
            bpt("TERM_LOAN"),
            bpt("PERSONAL_TERM_LOAN"),
            bpt("RENOVATION_TERM_LOAN")
        ].includes(type)) {
            return FEE_PRODUCT_TYPE.TERM_LOAN;
        }
        if (type === bpt("VENDOR_FINANCE")) return FEE_PRODUCT_TYPE.VENDOR_FINANCE;
        if (type === bpt("FACTORYING") || type === bpt("CEP")) {
            return FEE_PRODUCT_TYPE.FACTORING;
        }
        return null;
    }

    processingFee() {
        const onCase = get(this.props.caseDetail, "processing_fee_amount");
        if (onCase !== null && onCase !== undefined) return Number(onCase);
        const perChain = toArray(this.props.supplyChains)
            .map(chain => Number(get(chain, "processing_fee_amount")))
            .filter(value => !isNaN(value));
        return perChain.length ? Math.max.apply(null, perChain) : null;
    }

    /* ----------------------------------------------------------- actions */

    fetchSkipPod = () => {
        this.setSafeState(({ skipPod }) => ({ skipPod: { ...skipPod, loading: true } }));
        CaseService.getSkipPodStage(this.props.caseId)
            .then(({ data }) =>
                this.setSafeState({
                    skipPod: { loading: false, value: !!(data && data.is_skip_pod_stage) }
                })
            )
            .catch(() =>
                this.setSafeState(({ skipPod }) => ({ skipPod: { ...skipPod, loading: false } }))
            );
    };

    onSkipPodToggle = pending => {
        if (this.skipPodReadOnly()) return;
        this.setState({ skipPodModal: { pending, remarks: "", submitting: false } });
    };

    submitSkipPod = () => {
        const modal = this.state.skipPodModal;
        const remarks = (modal.remarks || "").trim();
        if (!remarks) {
            message.error("Please add remarks");
            return;
        }
        this.setState({ skipPodModal: { ...modal, submitting: true } });
        CaseService.skipPodStage(this.props.caseId, {
            is_skip_pod_stage: modal.pending,
            remarks
        })
            .then(() => {
                message.success(
                    modal.pending
                        ? "POD stage skipped successfully"
                        : "Skip POD stage decision reverted"
                );
                this.setSafeState({ skipPodModal: null }, this.fetchSkipPod);
            })
            .catch(error => {
                message.error(firstError(error, "Failed to update skip POD stage"));
                this.setSafeState({ skipPodModal: { ...modal, submitting: false } });
            });
    };

    /**
     * Putting the case on a fee program is what sets its processing fee, so
     * the dropdown writes straight through rather than opening a form.
     */
    onPickFeeProgram = value => {
        const feeMasterId = Number(value);
        if (!feeMasterId) return;
        const previous = this.state.feeProgram;
        this.setState({ feeProgram: feeMasterId, feeSaving: true });
        CompanyLosServices.addCaseFeeConfiguration({
            case_id: this.props.caseId,
            fee_master_id: feeMasterId
        })
            .then(() => {
                message.success("Fee program applied");
                this.setSafeState({ feeSaving: false });
                this.props.onRefresh();
            })
            .catch(error => {
                message.error(firstError(error, "Could not apply the fee program"));
                this.setSafeState({ feeProgram: previous, feeSaving: false });
            });
    };

    /* ------------------------------------------- charge programs (editable) */

    /**
     * The three charges a case can be re-pointed at a different program:
     * the fee program, the bounce charge and the pre-payment (foreclosure)
     * charge. Each is a pick-one from the seller's catalogue, posted to the
     * same endpoints the Account/Facility Details screen posts to.
     */
    programConfig = {
        fee: {
            title: "Change fee program",
            load: sellerId => CompanyLosServices.getFee(sellerId, this.feeProductType()),
            master: row => row && row.fee_master,
            describe: master =>
                toArray(master && master.configuration)
                    .map(slab =>
                        [
                            get(slab, "amount_or_percentage"),
                            get(slab, "description")
                        ]
                            .filter(part => part !== null && part !== undefined && part !== "")
                            .join(" ")
                    )
                    .filter(Boolean)
                    .join(", "),
            submit: (sellerId, caseId, id) =>
                CompanyLosServices.editCaseFee(sellerId, caseId, { fee_master: id })
        },
        bounce_charge: {
            title: "Change bounce charge",
            load: sellerId => CompanyLosServices.getBounceChargesData(sellerId),
            master: row => row && row.charge,
            describe: master => {
                const value = getIn(master, ["charge_json", "charges"]);
                return value === null || value === undefined ? "" : String(value);
            },
            submit: (sellerId, caseId, id) =>
                CompanyLosServices.editBounceCharges(sellerId, caseId, { charge: id })
        },
        pre_payment_charge: {
            title: "Change pre-payment charge",
            load: sellerId => CompanyLosServices.getPrePaymentChargesData(sellerId),
            master: row => row && row.foreclosure_master,
            describe: master =>
                toArray(master && master.configuration)
                    .map(slab =>
                        `${get(slab, "from_tenure")}–${get(slab, "to_tenure")}: ${get(slab, "value")}`
                    )
                    .join(", "),
            submit: (sellerId, caseId, id) =>
                CompanyLosServices.editPrePaymentCharge(sellerId, caseId, {
                    foreclosure_master: id
                })
        }
    };

    sellerId() {
        return (
            getIn(this.props.caseDetail, ["supply_chains", 0, "seller", "id"]) ||
            getIn(toArray(this.props.supplyChains)[0], ["seller", "id"])
        );
    }

    openProgram = type => {
        const config = this.programConfig[type];
        this.setState({
            programModal: { type, loading: true, submitting: false, options: [], selected: null }
        });
        config
            .load(this.sellerId())
            .then(({ data }) => {
                const rows =
                    (data && Array.isArray(data.results) && data.results) ||
                    (data && Array.isArray(data.data) && data.data) ||
                    (Array.isArray(data) ? data : []);
                const options = rows
                    .map(row => {
                        const master = config.master(row);
                        const id = master && (master.id || master);
                        if (!id) return null;
                        return {
                            id,
                            name: (master && master.name) || row.name || `Program ${id}`,
                            detail: config.describe(master),
                            // A program whose slab has already ended cannot be
                            // attached to a case.
                            expired: toArray(master && master.configuration).some(
                                slab =>
                                    slab &&
                                    slab.active_to &&
                                    moment(slab.active_to).isSameOrBefore(moment(), "day")
                            )
                        };
                    })
                    .filter(Boolean);
                this.setSafeState(({ programModal }) => ({
                    programModal: programModal
                        ? { ...programModal, options, loading: false }
                        : null
                }));
            })
            .catch(() => {
                message.error("Could not load the programs for this seller");
                this.setSafeState(({ programModal }) => ({
                    programModal: programModal ? { ...programModal, loading: false } : null
                }));
            });
    };

    submitProgram = () => {
        const modal = this.state.programModal;
        if (!modal.selected) {
            message.error("Pick a program first");
            return;
        }
        this.setState({ programModal: { ...modal, submitting: true } });
        this.programConfig[modal.type]
            .submit(this.sellerId(), this.props.caseId, modal.selected)
            .then(() => {
                message.success("Charge updated");
                this.setSafeState({ programModal: null });
                this.props.onRefresh();
            })
            .catch(error => {
                message.error(firstError(error, "Could not update the charge"));
                this.setSafeState({ programModal: { ...modal, submitting: false } });
            });
    };

    openUtr = () =>
        this.setState({
            utr: {
                date: moment().format("YYYY-MM-DD"),
                amount: this.processingFee() || "",
                merchant_id: "",
                transaction_detail: "",
                order_id: "",
                submitting: false
            }
        });

    setUtr = (key, value) =>
        this.setState(({ utr }) => ({ utr: { ...utr, [key]: value } }));

    addUtr = () => {
        const utr = this.state.utr;
        const values = {
            date: utr.date,
            amount: utr.amount,
            merchant_id: utr.merchant_id,
            transaction_detail: utr.transaction_detail,
            order_id: utr.order_id
        };
        const missing = Object.keys(values).filter(
            key => values[key] === "" || values[key] === null || values[key] === undefined
        );
        if (missing.length) {
            message.error("Every UTR field is required");
            return;
        }
        this.setState({ utr: { ...utr, submitting: true } });
        FacilityAccountService.addUTR({ case_id: this.props.caseId, ...values })
            .then(() => {
                message.success("UTR details added");
                this.setSafeState({ utr: null });
                this.props.onRefresh();
            })
            .catch(error => {
                message.error(firstError(error, "Failed to add UTR details"));
                this.setSafeState({ utr: { ...utr, submitting: false } });
            });
    };

    submitLms = () => {
        this.setState({ lmsSubmitting: true });
        Promise.resolve(this.props.onProceedToLms())
            .then(() => this.setSafeState({ lmsOpen: false, lmsSubmitting: false }))
            .catch(() => this.setSafeState({ lmsSubmitting: false }));
    };

    submitTermination = () => {
        const modal = this.state.terminateModal;
        this.setState({ terminateModal: { ...modal, submitting: true } });
        CaseService.terminateSanctionConditions(this.props.caseId, modal.remarks)
            .then(() => {
                message.success("Sanction condition terminated");
                this.setSafeState({ terminateModal: null });
                this.props.onRefresh();
            })
            .catch(() => {
                message.error("Failed to terminate condition");
                this.setSafeState({ terminateModal: { ...modal, submitting: false } });
            });
    };

    /* -------------------------------------------------- schedule actions */

    setDraft = (key, value) =>
        this.setState(({ draft }) => ({ draft: { ...draft, [key]: value } }));

    scheduleInputs() {
        const { draft } = this.state;
        const prefetch = this.state.prefetch || {};
        const principal =
            prefetch.sanction_limit !== undefined && prefetch.sanction_limit !== null
                ? prefetch.sanction_limit
                : get(this.props.caseDetail, "sanction_limit");
        return { principal, tenure: draft.tenure, draft };
    }

    generateSchedule = () => {
        const { principal, tenure, draft } = this.scheduleInputs();
        if (!tenure || !draft.disbursalDate) {
            message.error("Please fill tenure and tentative disbursal date");
            return;
        }
        this.setState({ busy: "generate" });
        FacilityAccountService.getGenerateScehduleBNPL(
            this.props.caseId,
            { principal, tenure },
            draft.disbursalDate,
            draft.emiType,
            false,
            draft.deductFirst,
            draft.moratoriumDays
        )
            .then(({ data }) => {
                if (data && Number(data.status) === 200) {
                    this.setSafeState({ preview: this.normalise(data.data), busy: null });
                } else {
                    message.error((data && data.message) || "Could not generate the schedule");
                    this.setSafeState({ preview: [], busy: null });
                }
            })
            .catch(error => {
                message.error(firstError(error, "Could not generate the schedule"));
                this.setSafeState({ preview: [], busy: null });
            });
    };

    normalise(rows) {
        return toArray(rows).map(row => ({
            instalment_nbr: row.instalment_nbr,
            emi_amount: row.aggregate_emi_amount || row.emi_amount,
            principal_amount: row.aggregate_principal_amount || row.principal_amount,
            interest_amount: row.aggregate_interest_amount || row.interest_amount,
            principal_receivable:
                row.aggregate_principal_receivable || row.principal_receivable,
            interest_paid_till_date:
                row.aggregate_interest_paid_till_date || row.interest_paid_till_date,
            tentative_due_date: row.tentative_due_date,
            upfront_interest_amount:
                row.aggregate_upfront_interest_amount || row.upfront_interest_amount
        }));
    }

    submitSchedule = () => {
        const { principal, tenure, draft } = this.scheduleInputs();
        this.setState({ busy: "submit" });
        FacilityAccountService.postEmiInstance(this.props.caseId, {
            principal,
            tenure,
            tentative_disbursal_date: draft.disbursalDate,
            moratorium_days: draft.moratoriumDays,
            emi_type: draft.emiType,
            add_fees_to_first_tranche: false,
            is_first_instalment_deducted_from_disbursal: draft.deductFirst
        })
            .then(({ data }) => {
                if (data && Number(data.status) === 200) {
                    message.success(data.message || "Schedule submitted");
                    this.setSafeState({ preview: [], busy: null });
                    this.props.onRefresh();
                } else {
                    message.error((data && data.message) || "Could not submit the schedule");
                    this.setSafeState({ busy: null });
                }
            })
            .catch(error => {
                message.error(firstError(error, "Could not submit the schedule"));
                this.setSafeState({ busy: null });
            });
    };

    saveSchedule = () => {
        const { principal, tenure, draft } = this.scheduleInputs();
        if (!tenure || !draft.disbursalDate) {
            message.error("Please fill tenure and tentative disbursal date");
            return;
        }
        this.setState({ busy: "save" });
        FacilityAccountService.saveScehduleBNPL(
            this.props.caseId,
            {
                tenure,
                from_date: moment(draft.disbursalDate),
                emiType: draft.emiType,
                is_first_instalment_deducted_from_disbursal: draft.deductFirst,
                moratorium_days: draft.moratoriumDays
            },
            principal
        )
            .then(() => {
                message.success("Details saved successfully");
                this.setSafeState({ busy: null });
                this.props.onRefresh();
            })
            .catch(error => {
                message.error(firstError(error, "Could not save the schedule"));
                this.setSafeState({ busy: null });
            });
    };

    resetSchedule = () => {
        this.setState({ busy: "reset", confirmReset: false });
        FacilityAccountService.resetEmiSchedule(this.props.caseId)
            .then(() => {
                message.success("Schedule reset");
                this.setSafeState({ busy: null, preview: [] });
                this.props.onRefresh();
            })
            .catch(() => {
                message.error("Could not reset the schedule");
                this.setSafeState({ busy: null });
            });
    };

    /* ------------------------------------------------------------ render */

    renderActionBar() {
        const { caseDetail } = this.props;
        const prepaid = get(caseDetail, "prepaid_processing_fee");
        const paid = get(caseDetail, "is_processing_fee_paid");
        const fee = this.processingFee();
        const createdOnLms = get(caseDetail, "is_created_on_lms");

        return (
            <Card className={"ops-ws__actionbar"}>
                <Metric label={"Processing fee"} value={fee === null ? "—" : money(fee)} />

                <Metric label={"PF collected at disbursal"} value={prepaid ? "Yes" : "No"}>
                    {prepaid ? (
                        paid ? (
                            <Pill tone={"ok"}>{"Paid"}</Pill>
                        ) : (
                            <button
                                type={"button"}
                                className={"ops-ws__btn"}
                                disabled={!this.canEdit()}
                                onClick={this.openUtr}
                            >
                                {"Add UTR"}
                            </button>
                        )
                    ) : null}
                </Metric>

                {this.skipPodApplicable() ? (
                    <Toggle
                        label={"Skip POD stage"}
                        on={this.state.skipPod.value}
                        disabled={this.skipPodReadOnly() || this.state.skipPod.loading}
                        onChange={this.onSkipPodToggle}
                    />
                ) : null}

                <div className={"ops-ws__actionbar-spacer"} />

                {createdOnLms ? (
                    <span className={"ops-ws__btn ops-ws__btn--done"}>
                        <Icon d={ICONS.check} size={14} />
                        {"Moved to LMS"}
                    </span>
                ) : (
                    <button
                        type={"button"}
                        className={"ops-ws__btn ops-ws__btn--primary"}
                        disabled={!this.canEdit() || this.losToLmsDisabled()}
                        onClick={() => this.setState({ lmsOpen: true })}
                    >
                        <Icon d={ICONS.arrow} size={14} />
                        {"LOS to LMS"}
                    </button>
                )}
            </Card>
        );
    }

    renderPricing() {
        const pricing = this.state.pricing;
        if (!pricing) return null;
        const pf = pricing.pf;
        const pfType = Number(pricing.pf_type);
        const stats = [
            { label: "ROI", value: pricing.roi != null ? `${pricing.roi}%` : "—" },
            {
                label: "PF",
                value:
                    pf == null
                        ? "—"
                        : pfType === 2
                            ? `${pf}%`
                            : pfType === 1
                                ? money(pf)
                                : String(pf)
            },
            { label: "Tenor", value: pricing.tenor != null ? `${pricing.tenor}M` : "—" },
            { label: "EMI type", value: pricing.emi_type != null ? String(pricing.emi_type) : "—" }
        ];
        const status = pricing.approval_status
            ? String(pricing.approval_status).trim().toLowerCase()
            : null;
        const tone =
            status === "approve"
                ? "ok"
                : status && status.indexOf("refer_back") === 0
                    ? "warn"
                    : "info";

        return (
            <Card>
                <CardHead
                    title={"Pricing"}
                    sub={
                        pricing.updated_at
                            ? `Last approved ${moment(pricing.updated_at).format("DD MMM YYYY, h:mm A")}`
                            : null
                    }
                    extra={
                        status ? <Pill tone={tone}>{status.replace(/_/g, " ")}</Pill> : null
                    }
                />
                <div className={"ops-ws__stats"}>
                    {stats.map(stat => (
                        <div key={stat.label} className={"ops-ws__stat"}>
                            <div className={"ops-ws__stat-label"}>{stat.label}</div>
                            <div className={"ops-ws__stat-value"}>{stat.value}</div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    /**
     * Processing fee and charges — one card.
     *
     * The agreement's schedule of charges (`bnpl/charge/schedule/`) and the
     * case's own defaults describe the same four numbers, so they are stated
     * once: the schedule value when the backend has computed one, the case
     * default otherwise. Each row carries the program it is charged under and,
     * for an operator who may change it, the control that changes it.
     */
    renderCharges() {
        const { caseDetail, charges } = this.props;
        const editable = this.canEdit() && !get(caseDetail, "is_created_on_lms");
        const pick = (fromSchedule, fromCase) =>
            fromSchedule !== null && fromSchedule !== undefined && fromSchedule !== ""
                ? fromSchedule
                : fromCase;

        const preProgram = toArray(
            getIn(caseDetail, ["default_foreclosure_charges", "configuration"])
        )
            .map(slab => get(slab, "description"))
            .filter(Boolean)
            .join(", ");

        const rows = [
            {
                key: "fee",
                label: "Processing fee",
                value: chargeText(
                    pick(charges.processing_fee, get(caseDetail, "processing_fee_amount"))
                ),
                program: [
                    get(caseDetail, "default_processing_fee"),
                    getIn(caseDetail, ["default_processing_charges", "description"])
                ]
                    .filter(part => part !== null && part !== undefined && part !== "")
                    .join(" "),
                // The fee is whichever program the case is on, so it is picked
                // from the seller's catalogue rather than typed.
                dropdown: true
            },
            {
                key: "documentation",
                label: "Documentation / stamp charge",
                value: chargeText(
                    pick(charges.documentation_charges, get(caseDetail, "documentation_charges"))
                )
            },
            {
                key: "bounce_charge",
                label: "Cheque bounce charge",
                value: chargeText(
                    pick(charges.cheque_bounce_charges, get(caseDetail, "default_bounce_charges"))
                ),
                program: getIn(caseDetail, [
                    "default_bounce_charges",
                    "configuration",
                    "charge_json",
                    "charges"
                ]),
                onEdit: () => this.openProgram("bounce_charge")
            },
            {
                key: "pre_payment_charge",
                label: "Foreclosure / pre-payment charge",
                value: chargeText(
                    charges.foreclosure_charges ||
                        get(caseDetail, "default_foreclosure_charges")
                ),
                program: preProgram,
                onEdit: () => this.openProgram("pre_payment_charge")
            }
        ];

        const slabs = toArray(charges.foreclosure_charges).filter(
            slab => slab && typeof slab === "object"
        );

        return (
            <Card>
                <CardHead
                    title={"Processing fee & charges"}
                    sub={"The schedule of charges as it prints on the kit."}
                />
                <div className={"ops-ws__card-body ops-ws__kv-list"}>
                    {rows.map(row =>
                        row.dropdown ? (
                            <div key={row.key} className={"ops-ws__charge-row is-select"}>
                                <div className={"ops-ws__charge-main"}>
                                    <span className={"ops-ws__param-label"}>{row.label}</span>
                                    <select
                                        className={"ops-ws__select"}
                                        value={this.state.feeProgram || ""}
                                        disabled={
                                            !editable ||
                                            this.state.feeProgramsLoading ||
                                            this.state.feeSaving
                                        }
                                        onChange={e => this.onPickFeeProgram(e.target.value)}
                                    >
                                        <option value={""}>
                                            {this.state.feeProgramsLoading
                                                ? "Loading fee programs…"
                                                : row.program || "Select a fee program"}
                                        </option>
                                        {this.state.feePrograms.map(option => (
                                            <option
                                                key={option.id}
                                                value={option.id}
                                                disabled={option.expired}
                                            >
                                                {option.name}
                                                {option.expired ? " (ended)" : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <span className={"ops-ws__charge-value"}>
                                    {this.state.feeSaving ? "Saving…" : row.value || "—"}
                                </span>
                                <span className={"ops-ws__charge-spacer"} />
                            </div>
                        ) : (
                            <div key={row.key} className={"ops-ws__charge-row"}>
                                <div className={"ops-ws__charge-main"}>
                                    <span className={"ops-ws__param-label"}>{row.label}</span>
                                    {row.program ? (
                                        <span className={"ops-ws__charge-program"}>
                                            {row.program}
                                        </span>
                                    ) : null}
                                </div>
                                <span className={"ops-ws__charge-value"}>{row.value || "—"}</span>
                                {row.onEdit ? (
                                    <button
                                        type={"button"}
                                        className={"ops-ws__icon-btn"}
                                        title={`Change ${row.label.toLowerCase()}`}
                                        disabled={!editable}
                                        onClick={row.onEdit}
                                    >
                                        <Icon d={ICONS.pen} size={13} />
                                    </button>
                                ) : (
                                    <span className={"ops-ws__charge-spacer"} />
                                )}
                            </div>
                        )
                    )}
                </div>
                {slabs.length ? (
                    <div className={"ops-ws__table-scroll"}>
                        <table className={"ops-ws__table"}>
                            <thead>
                                <tr>
                                    <th>{"From tenure"}</th>
                                    <th>{"To tenure"}</th>
                                    <th>{"Foreclosure charge"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {slabs.map((slab, index) => (
                                    <tr key={index}>
                                        <td>{get(slab, "from_tenure")}</td>
                                        <td>{get(slab, "to_tenure")}</td>
                                        <td>{get(slab, "value")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
                <div className={"ops-ws__card-foot"}>
                    <span className={"ops-ws__hint"}>
                        {editable
                            ? "The processing fee follows the fee program; changing it after the kit is generated needs the agreement re-triggered."
                            : "Charges are locked once the case is created on LMS."}
                    </span>
                </div>
            </Card>
        );
    }

    renderMapping() {
        const chains = toArray(this.props.supplyChains);
        return (
            <Card>
                <CardHead
                    title={"Mapping"}
                    sub={"Seller, anchor and the scheme mapped to them."}
                />
                <div className={"ops-ws__card-body"}>
                    {chains.length ? (
                        chains.map((chain, index) => {
                            const scheme = parseSchemeDescription(
                                get(chain, "scheme_description")
                            );
                            const flexible = parseSchemeDescription(
                                get(chain, "dwd_sp_description")
                            );
                            return (
                                <div
                                    key={get(chain, "id") || index}
                                    className={"ops-ws__chain" + (index ? " is-next" : "")}
                                >
                                    <div className={"ops-ws__kv-grid"}>
                                        <KeyValue
                                            label={"Seller"}
                                            value={getIn(chain, ["seller", "name"])}
                                        />
                                        <KeyValue
                                            label={"Anchor"}
                                            value={getIn(chain, ["anchor", "name"])}
                                        />
                                        <KeyValue
                                            label={"Processing fee"}
                                            value={money(get(chain, "processing_fee_amount"))}
                                        />
                                        <KeyValue
                                            label={"Sub-limit"}
                                            value={money(get(chain, "sub_limit"))}
                                        />
                                        <KeyValue
                                            label={"Mapped by"}
                                            value={personName(get(chain, "updated_by_user"))}
                                            tone={"muted"}
                                        />
                                    </div>

                                    <div className={"ops-ws__scheme"}>
                                        <div className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>
                                            {get(chain, "scheme_name") || "Scheme"}
                                        </div>
                                        {scheme.length ? (
                                            <div className={"ops-ws__scheme-grid"}>
                                                {scheme.map((row, key) => (
                                                    <div
                                                        key={`${row.label || "row"}-${key}`}
                                                        className={
                                                            "ops-ws__scheme-item" +
                                                            (row.label ? "" : " is-wide")
                                                        }
                                                    >
                                                        {row.label ? (
                                                            <span className={"ops-ws__scheme-label"}>
                                                                {row.label}
                                                            </span>
                                                        ) : null}
                                                        <span className={"ops-ws__scheme-value"}>
                                                            {row.value || "—"}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className={"ops-ws__note"}>
                                                {"No scheme mapped to this supply chain yet."}
                                            </div>
                                        )}
                                        {flexible.length ? (
                                            <React.Fragment>
                                                <div
                                                    className={"ops-ws__eyebrow"}
                                                    style={{ marginTop: 12 }}
                                                >
                                                    {"Flexible parameters"}
                                                </div>
                                                <div className={"ops-ws__scheme-grid"}>
                                                    {flexible.map((row, key) => (
                                                        <div
                                                            key={`${row.label || "row"}-${key}`}
                                                            className={
                                                                "ops-ws__scheme-item" +
                                                                (row.label ? "" : " is-wide")
                                                            }
                                                        >
                                                            {row.label ? (
                                                                <span
                                                                    className={"ops-ws__scheme-label"}
                                                                >
                                                                    {row.label}
                                                                </span>
                                                            ) : null}
                                                            <span className={"ops-ws__scheme-value"}>
                                                                {row.value || "—"}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </React.Fragment>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className={"ops-ws__note"}>
                            {"No supply chain mapped to this case yet."}
                        </div>
                    )}
                </div>
            </Card>
        );
    }

    renderLimits() {
        const { caseDetail } = this.props;
        return (
            <Card>
                <CardHead title={"Limits"} />
                <div className={"ops-ws__card-body ops-ws__kv-list"}>
                    <KeyValue label={"Proposed limit"} value={money(get(caseDetail, "proposed_limit"))} />
                    <KeyValue label={"Sanction limit"} value={money(get(caseDetail, "sanction_limit"))} />
                    <KeyValue label={"Existing limit"} value={money(get(caseDetail, "existing_limit"))} />
                    <div>
                        <div className={"ops-ws__kv-label"}>{"Created on LMS"}</div>
                        <div style={{ marginTop: 3 }}>
                            <Pill tone={get(caseDetail, "is_created_on_lms") ? "ok" : "warn"}>
                                {get(caseDetail, "is_created_on_lms") ? "Yes" : "Not yet"}
                            </Pill>
                        </div>
                    </div>
                </div>
            </Card>
        );
    }

    renderSanction() {
        const { caseDetail } = this.props;
        const condition = get(caseDetail, "sanction_condition");
        const title = condition ? get(condition, "title") : null;
        const status = Number(get(caseDetail, "sanction_condition_status"));
        const canTerminate =
            this.canEdit() &&
            status === 2 &&
            Boolean(get(caseDetail, "can_terminate_sanction_condition"));
        // Attaching one is a per-bank template with dynamic fields; it is not
        // rebuilt here, so say where it lives instead of hiding it.
        const canAdd = !condition && getIn(caseDetail, ["bank", "code"]) === "MF";

        return (
            <Card>
                <CardHead
                    title={"Sanction conditions"}
                    extra={
                        status === 2 ? (
                            <Pill tone={"info"}>{"Active"}</Pill>
                        ) : status === 4 ? (
                            <Pill tone={"bad"}>{"Terminated"}</Pill>
                        ) : null
                    }
                />
                <div className={"ops-ws__card-body ops-ws__kv-list"}>
                    <KeyValue label={"Sanction remarks"} value={get(caseDetail, "sanction_remarks")} />
                    <KeyValue label={"Sanction condition"} value={title} />
                    {get(caseDetail, "sanction_condition_status_remarks") ? (
                        <KeyValue
                            label={"Status remarks"}
                            value={get(caseDetail, "sanction_condition_status_remarks")}
                            tone={"muted"}
                        />
                    ) : null}
                </div>
                {canTerminate ? (
                    <div className={"ops-ws__card-foot"}>
                        <button
                            type={"button"}
                            className={"ops-ws__btn"}
                            onClick={() =>
                                this.setState({ terminateModal: { remarks: "", submitting: false } })
                            }
                        >
                            {"Terminate sanction condition"}
                        </button>
                    </div>
                ) : canAdd ? (
                    <div className={"ops-ws__card-foot"}>
                        <span className={"ops-ws__hint"}>
                            {"Attaching a new sanction condition is a templated form and still lives on the Account/Facility Details tab."}
                        </span>
                    </div>
                ) : null}
            </Card>
        );
    }

    renderLoanTerms() {
        const { loanTerms, caseDetail } = this.props;
        return (
            <Card>
                <CardHead title={"Loan terms"} sub={"Saved on the case."} />
                <div className={"ops-ws__card-body"}>
                    {loanTerms ? (
                        <div className={"ops-ws__kv-grid"}>
                            <KeyValue
                                label={"Loan amount (principal)"}
                                value={money(
                                    loanTerms.principal ||
                                        get(caseDetail, "sanction_limit") ||
                                        get(caseDetail, "proposed_limit")
                                )}
                            />
                            <KeyValue label={"Tenure"} value={loanTerms.tenure} />
                            <KeyValue
                                label={"Tentative disbursal date"}
                                value={date(loanTerms.tentative_disbursal_date)}
                            />
                            <KeyValue label={"EMI type"} value={loanTerms.emi_type} />
                            <KeyValue label={"Moratorium days"} value={loanTerms.moratorium_days} />
                            <KeyValue
                                label={"First instalment deducted at disbursal"}
                                value={
                                    loanTerms.is_first_instalment_deducted_from_disbursal
                                        ? "Yes"
                                        : "No"
                                }
                            />
                        </div>
                    ) : (
                        <div className={"ops-ws__note"}>
                            {"No saved loan terms yet — generate and save the schedule below."}
                        </div>
                    )}
                </div>
            </Card>
        );
    }

    /** Scheme-derived parameters the operator cannot change, read as a block. */
    renderScheduleParams() {
        const prefetch = this.state.prefetch;
        if (!prefetch) return null;
        const items = [
            { label: "Rate of interest", value: prefetch.min_normal_roi || prefetch.roi },
            {
                label: "EMI frequency",
                value: labelFor(INSTALMENT_FREQUENCY, prefetch.instalment_frequency)
            },
            {
                label: "EMI date type",
                value: labelFor(INSTALMENT_DATE_TYPE, prefetch.instalment_date_type)
            },
            { label: "Interest type", value: labelFor(INTEREST_TYPE, prefetch.interest_type) },
            {
                label: "Moratorium interest charged",
                value: prefetch.is_moratorium_interest_charged ? "Yes" : "No"
            },
            { label: "Margin", value: prefetch.withhold_perc },
            { label: "Buyer subvention days", value: prefetch.buyer_subvention_days },
            { label: "Seller subvention days", value: prefetch.seller_subvention_days },
            {
                label: "Moratorium period end date",
                value:
                    prefetch.moratorium_period_end_date ||
                    prefetch.tentative_moratorium_period_end_date
            },
            {
                label: "Moratorium interest amount",
                value:
                    prefetch.sum_moratorium_period_interest_amount ||
                    prefetch.sum_tentative_moratorium_period_interest_amount
            }
        ];
        return (
            <Card>
                <CardHead
                    title={"Scheme parameters"}
                    sub={"Read-only — these come from the scheme attached to the case."}
                />
                <div className={"ops-ws__card-body"}>
                    <div className={"ops-ws__kv-grid"}>
                        {items.map(item => (
                            <KeyValue key={item.label} label={item.label} value={item.value} />
                        ))}
                    </div>
                </div>
            </Card>
        );
    }

    renderScheduleBuilder() {
        const { draft, busy, preview } = this.state;
        const prefetch = this.state.prefetch || {};
        const locked = this.props.emiSchedule.length > 0;
        const saveStage = SCHEDULE_SAVE_STAGE_IDS.includes(Number(this.props.stageId));
        const principal =
            prefetch.sanction_limit !== undefined && prefetch.sanction_limit !== null
                ? prefetch.sanction_limit
                : get(this.props.caseDetail, "sanction_limit");
        const readOnly = locked || !this.canEdit();
        // The same guards the Account/Facility Details screen applies: a
        // tenure of at least one instalment, and — before the terms can be
        // saved — the three charge defaults the schedule is priced off.
        const tenureError =
            draft.tenure !== "" && Number(draft.tenure) < 1
                ? "Tenure has to be at least 1."
                : null;
        const missingCharges = [
            !get(this.props.caseDetail, "default_foreclosure_charges") && "foreclosure",
            !get(this.props.caseDetail, "default_bounce_charges") && "bounce",
            get(this.props.caseDetail, "default_processing_fee") === null ||
            get(this.props.caseDetail, "default_processing_fee") === undefined
                ? "processing fee"
                : null
        ].filter(Boolean);
        const ready = Boolean(draft.tenure) && Boolean(draft.disbursalDate) && !tenureError;

        return (
            <Card>
                <CardHead
                    title={"Schedule builder"}
                    sub={
                        locked
                            ? "A schedule already exists for this case. Reset it to build a new one."
                            : saveStage
                                ? "Save the terms the schedule will be built from."
                                : "Generate a schedule, review it, then submit."
                    }
                />
                <div className={"ops-ws__card-body"}>
                    <div className={"ops-ws__kv-grid ops-ws__kv-grid--wide"}>
                        <div className={"ops-ws__field"}>
                            <div className={"ops-ws__field-label"}>{"Loan amount (principal)"}</div>
                            <input
                                className={"ops-ws__input"}
                                value={principal === null || principal === undefined ? "" : principal}
                                readOnly
                            />
                        </div>
                        <div className={"ops-ws__field"}>
                            <div className={"ops-ws__field-label"}>{"Tenure"}</div>
                            <input
                                className={
                                    "ops-ws__input" + (tenureError ? " ops-ws__input--warn" : "")
                                }
                                type={"number"}
                                min={1}
                                value={draft.tenure === null || draft.tenure === undefined ? "" : draft.tenure}
                                disabled={readOnly}
                                onChange={e => this.setDraft("tenure", e.target.value)}
                            />
                            {tenureError ? (
                                <div className={"ops-ws__field-error"}>{tenureError}</div>
                            ) : null}
                        </div>
                        <div className={"ops-ws__field"}>
                            <div className={"ops-ws__field-label"}>{"Tentative disbursal date"}</div>
                            <input
                                className={"ops-ws__input"}
                                type={"date"}
                                min={moment().format("YYYY-MM-DD")}
                                value={draft.disbursalDate || ""}
                                disabled={readOnly}
                                onChange={e => this.setDraft("disbursalDate", e.target.value)}
                            />
                        </div>
                        <div className={"ops-ws__field"}>
                            <div className={"ops-ws__field-label"}>
                                {`Moratorium days${
                                    prefetch.max_moratorium_period
                                        ? ` (0–${prefetch.max_moratorium_period})`
                                        : ""
                                }`}
                            </div>
                            <input
                                className={"ops-ws__input"}
                                value={
                                    draft.moratoriumDays === null || draft.moratoriumDays === undefined
                                        ? ""
                                        : draft.moratoriumDays
                                }
                                readOnly
                            />
                        </div>
                        <div className={"ops-ws__field"}>
                            <div className={"ops-ws__field-label"}>{"EMI type"}</div>
                            <Segmented
                                value={String(draft.emiType)}
                                disabled={readOnly}
                                onChange={value => this.setDraft("emiType", value)}
                                options={[
                                    { value: "1", label: "Arrears" },
                                    { value: "2", label: "Advance" }
                                ]}
                            />
                        </div>
                        <div className={"ops-ws__field"}>
                            <div
                                className={"ops-ws__field-label"}
                                title={"Deduct first instalment at disbursal"}
                            >
                                {"Deduct 1st EMI at disbursal"}
                            </div>
                            <Segmented
                                value={draft.deductFirst ? "yes" : "no"}
                                disabled={readOnly}
                                onChange={value => this.setDraft("deductFirst", value === "yes")}
                                options={[
                                    { value: "yes", label: "Yes" },
                                    { value: "no", label: "No" }
                                ]}
                            />
                        </div>
                    </div>
                </div>
                <div className={"ops-ws__card-foot"}>
                    {locked ? (
                        <React.Fragment>
                            {/* Reset throws the generated schedule away, so it
                                asks before it fires. */}
                            <button
                                type={"button"}
                                className={
                                    "ops-ws__btn" +
                                    (this.state.confirmReset ? " ops-ws__btn--primary" : "")
                                }
                                disabled={
                                    !this.canEdit() || !this.canResetSchedule() || busy === "reset"
                                }
                                onClick={
                                    this.state.confirmReset
                                        ? this.resetSchedule
                                        : () => this.setState({ confirmReset: true })
                                }
                            >
                                <Icon d={ICONS.refresh} size={14} />
                                {busy === "reset"
                                    ? "Resetting…"
                                    : this.state.confirmReset
                                        ? "Confirm reset"
                                        : "Reset schedule"}
                            </button>
                            {this.state.confirmReset ? (
                                <button
                                    type={"button"}
                                    className={"ops-ws__link"}
                                    onClick={() => this.setState({ confirmReset: false })}
                                >
                                    {"Cancel"}
                                </button>
                            ) : null}
                            <span className={"ops-ws__hint"}>
                                {!this.canResetSchedule()
                                    ? "Reset is only available while the case sits on the ops stage."
                                    : this.state.confirmReset
                                        ? "This clears the generated schedule on the case."
                                        : "Resetting clears the generated schedule on the case."}
                            </span>
                        </React.Fragment>
                    ) : saveStage ? (
                        <React.Fragment>
                            <button
                                type={"button"}
                                className={"ops-ws__btn ops-ws__btn--primary"}
                                disabled={
                                    !this.canEdit() ||
                                    busy === "save" ||
                                    !ready ||
                                    missingCharges.length > 0
                                }
                                onClick={this.saveSchedule}
                            >
                                {busy === "save" ? "Saving…" : "Save schedule"}
                            </button>
                            {missingCharges.length ? (
                                <span className={"ops-ws__hint"}>
                                    {`Set the ${missingCharges.join(", ")} charge${
                                        missingCharges.length > 1 ? "s" : ""
                                    } on the case before saving the terms.`}
                                </span>
                            ) : null}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <button
                                type={"button"}
                                className={"ops-ws__btn ops-ws__btn--primary"}
                                disabled={!this.canEdit() || busy === "generate" || !ready}
                                onClick={this.generateSchedule}
                            >
                                {busy === "generate" ? "Generating…" : "Generate schedule"}
                            </button>
                            {preview.length ? (
                                <button
                                    type={"button"}
                                    className={"ops-ws__btn ops-ws__btn--go"}
                                    disabled={busy === "submit"}
                                    onClick={this.submitSchedule}
                                >
                                    {busy === "submit" ? "Submitting…" : "Submit schedule"}
                                </button>
                            ) : null}
                        </React.Fragment>
                    )}
                </div>
            </Card>
        );
    }

    renderEmiTable() {
        const preview = this.state.preview;
        const rows = preview.length ? preview : this.props.emiSchedule;
        return (
            <Card>
                <CardHead
                    title={"EMI schedule"}
                    extra={
                        preview.length ? (
                            <Pill tone={"warn"}>{"Preview — not submitted"}</Pill>
                        ) : null
                    }
                />
                {rows.length ? (
                    <div className={"ops-ws__table-scroll"}>
                        <table className={"ops-ws__table"}>
                            <thead>
                                <tr>
                                    {EMI_COLUMNS.map(column => (
                                        <th key={column.key}>{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={row.instalment_nbr || index}>
                                        {EMI_COLUMNS.map(column => (
                                            <td key={column.key}>
                                                {column.money
                                                    ? money(row[column.key])
                                                    : column.date
                                                        ? date(row[column.key])
                                                        : row[column.key] === null ||
                                                          row[column.key] === undefined
                                                            ? "—"
                                                            : row[column.key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={"ops-ws__card-body"}>
                        <EmptyState>
                            {"No EMI schedule generated for this case yet."}
                        </EmptyState>
                    </div>
                )}
            </Card>
        );
    }

    /* ------------------------------------------------------------ modals */

    renderProgramModal() {
        const modal = this.state.programModal;
        const config = modal ? this.programConfig[modal.type] : null;
        return (
            <Modal
                visible={Boolean(modal)}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={560}
                title={config ? config.title : ""}
                okText={"Save"}
                confirmLoading={Boolean(modal && modal.submitting)}
                onOk={this.submitProgram}
                onCancel={() => this.setState({ programModal: null })}
            >
                {modal ? (
                    modal.loading ? (
                        <div className={"ops-ws__spin"}>
                            <Spin />
                        </div>
                    ) : modal.options.length ? (
                        <div className={"ops-ws__pick-list"}>
                            {modal.options.map(option => (
                                <button
                                    key={option.id}
                                    type={"button"}
                                    className={
                                        "ops-ws__pick" +
                                        (modal.selected === option.id ? " is-active" : "")
                                    }
                                    disabled={option.expired}
                                    onClick={() =>
                                        this.setState({
                                            programModal: { ...modal, selected: option.id }
                                        })
                                    }
                                >
                                    <span className={"ops-ws__pick-name"}>
                                        {option.name}
                                        {option.expired ? (
                                            <Pill tone={"bad"}>{"Ended"}</Pill>
                                        ) : null}
                                    </span>
                                    {option.detail ? (
                                        <span className={"ops-ws__pick-detail"}>
                                            {option.detail}
                                        </span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <EmptyState>
                            {"No program is configured for this seller yet."}
                        </EmptyState>
                    )
                ) : null}
            </Modal>
        );
    }

    renderSkipPodModal() {
        const modal = this.state.skipPodModal;
        return (
            <Modal
                visible={Boolean(modal)}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={480}
                title={modal && modal.pending ? "Skip POD stage" : "Revert skip POD stage"}
                okText={"Submit"}
                confirmLoading={Boolean(modal && modal.submitting)}
                onOk={this.submitSkipPod}
                onCancel={() => this.setState({ skipPodModal: null })}
            >
                {modal ? (
                    <React.Fragment>
                        <div className={"ops-ws__field-label"}>{"Remarks"}</div>
                        <textarea
                            className={"ops-ws__textarea"}
                            placeholder={"Enter remarks"}
                            value={modal.remarks}
                            onChange={e =>
                                this.setState({
                                    skipPodModal: { ...modal, remarks: e.target.value }
                                })
                            }
                        />
                    </React.Fragment>
                ) : null}
            </Modal>
        );
    }

    renderUtrModal() {
        const utr = this.state.utr;
        const fields = [
            { key: "date", label: "Transaction date", type: "date" },
            { key: "amount", label: "Processing fee amount", type: "number" },
            { key: "merchant_id", label: "Merchant ID" },
            { key: "transaction_detail", label: "Transaction detail" },
            { key: "order_id", label: "Order ID" }
        ];
        return (
            <Modal
                visible={Boolean(utr)}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={560}
                title={"Add UTR details"}
                okText={"Save"}
                confirmLoading={Boolean(utr && utr.submitting)}
                onOk={this.addUtr}
                onCancel={() => this.setState({ utr: null })}
            >
                {utr ? (
                    <div className={"ops-ws__kv-grid"}>
                        {fields.map(field => (
                            <div key={field.key} className={"ops-ws__field"}>
                                <div className={"ops-ws__field-label"}>{field.label}</div>
                                <input
                                    className={"ops-ws__input"}
                                    type={field.type || "text"}
                                    value={utr[field.key]}
                                    onChange={e => this.setUtr(field.key, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}
            </Modal>
        );
    }

    renderLmsModal() {
        const checklist = this.props.lmsChecklist || [];
        const passed = checklist.every(item => item.ok);
        return (
            <Modal
                visible={this.state.lmsOpen}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={540}
                title={"Proceed checklist"}
                okText={passed ? "Proceed" : "Review case"}
                confirmLoading={this.state.lmsSubmitting}
                onOk={passed ? this.submitLms : () => this.setState({ lmsOpen: false })}
                onCancel={() => this.setState({ lmsOpen: false })}
            >
                <div className={"ops-ws__check-list"}>
                    {checklist.map(item => (
                        <div key={item.label} className={"ops-ws__check-row"}>
                            <StatusBadge tone={item.ok ? "ok" : "bad"} small />
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
                {!passed ? (
                    <div className={"ops-ws__hint"} style={{ marginTop: 12 }}>
                        {"Everything above has to pass before the case can be created on LMS."}
                    </div>
                ) : null}
            </Modal>
        );
    }

    renderTerminateModal() {
        const modal = this.state.terminateModal;
        return (
            <Modal
                visible={Boolean(modal)}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={480}
                title={"Terminate sanction condition"}
                okText={"Terminate"}
                confirmLoading={Boolean(modal && modal.submitting)}
                onOk={this.submitTermination}
                onCancel={() => this.setState({ terminateModal: null })}
            >
                {modal ? (
                    <React.Fragment>
                        <div className={"ops-ws__field-label"}>{"Termination remarks"}</div>
                        <textarea
                            className={"ops-ws__textarea"}
                            placeholder={"Enter remarks"}
                            value={modal.remarks || ""}
                            onChange={e =>
                                this.setState({
                                    terminateModal: { ...modal, remarks: e.target.value }
                                })
                            }
                        />
                    </React.Fragment>
                ) : null}
            </Modal>
        );
    }

    render() {
        const { hasTermLoan } = this.props;

        // Facility and Term Loan used to be two views behind a toggle; they
        // are one screen now. Cards pair up row by row so the two cards in a
        // row share a height — independent column stacks ended at different
        // heights and left dead space under the shorter one. A card left
        // without a partner takes the full row instead of leaving a hole.
        const cards = [
            this.renderMapping(),
            this.renderCharges(),
            this.renderLimits(),
            this.renderSanction(),
            hasTermLoan ? this.renderLoanTerms() : null,
            hasTermLoan ? this.renderScheduleParams() : null
        ].filter(Boolean);

        return (
            <div className={"ops-ws__section"}>
                <SectionHeading
                    title={"Account / Facility"}
                    subtitle={
                        hasTermLoan
                            ? "Scheme, charges, sub-limits and the term-loan EMI schedule."
                            : "Scheme, sub-limit and processing fee for the mapped supply chains."
                    }
                />

                {this.renderActionBar()}
                {this.renderPricing()}

                <div className={"ops-ws__grid ops-ws__grid--pairs"}>
                    {cards.map((card, index) => (
                        <div
                            key={index}
                            className={
                                "ops-ws__cell" +
                                (index === cards.length - 1 && cards.length % 2
                                    ? " ops-ws__cell--full"
                                    : "")
                            }
                        >
                            {card}
                        </div>
                    ))}
                </div>

                {hasTermLoan ? (
                    <React.Fragment>
                        {this.renderScheduleBuilder()}
                        {this.renderEmiTable()}
                    </React.Fragment>
                ) : null}

                {this.renderProgramModal()}
                {this.renderSkipPodModal()}
                {this.renderUtrModal()}
                {this.renderLmsModal()}
                {this.renderTerminateModal()}
            </div>
        );
    }
}

AccountFacilitySection.defaultProps = {
    charges: {},
    emiSchedule: [],
    lmsChecklist: [],
    onRefresh: () => {},
    onProceedToLms: () => {}
};

AccountFacilitySection.propTypes = {
    caseId: PropTypes.number,
    caseDetail: PropTypes.object,
    facility: PropTypes.object,
    supplyChains: PropTypes.any,
    emiSchedule: PropTypes.array.isRequired,
    charges: PropTypes.object.isRequired,
    loanTerms: PropTypes.object,
    hasTermLoan: PropTypes.bool,
    stageId: PropTypes.number,
    user: PropTypes.object,
    recreditViewOnly: PropTypes.bool,
    lmsChecklist: PropTypes.array,
    onProceedToLms: PropTypes.func,
    onRefresh: PropTypes.func
};

export default AccountFacilitySection;
